import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Settings as SettingsIcon,
  People as PeopleIcon,
  Analytics as AnalyticsIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import UserManagement from '../admin/UserManagement';
import SystemSettings from '../admin/SystemSettings';
import Analytics from '../admin/Analytics';
import DepartmentManagement from '../admin/DepartmentManagement';
import { AIConfigurationManager } from '../AIConfigurationManager';
import { AIConsole } from '../ai-console';
import AIUsageMonitor from '../admin/AIUsageMonitor';
import AdminService, { AdminStats, SystemHealth, RecentActivity } from '../../services/AdminService';
import { usePermissions, PermissionGuard, useConditionalRender } from '../../contexts/PermissionsContext';

/**
 * Admin Dashboard Component
 * Dashboard for system administrators with user management and system controls
 */
const AdminDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [systemHealth, setSystemHealth] = useState<SystemHealth | null>(null);
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Permission hooks
  const { hasPermission } = usePermissions();
  const { renderIfPermission, renderIfAnyPermission } = useConditionalRender();

  // Available tabs based on permissions
  const availableTabs = [
    { label: 'Overview', permissions: [], alwaysShow: true },
    { label: 'AI Console', permissions: [], alwaysShow: true },
    { label: 'User Management', permissions: ['user.view', 'role.view'] },
    { label: 'Departments', permissions: ['settings.view', 'settings.manage'] },
    { label: 'AI Configuration', permissions: ['settings.view', 'settings.manage'] },
    { label: 'AI Usage & Costs', permissions: ['settings.view', 'analytics.view'] },
    { label: 'System Settings', permissions: ['settings.view', 'settings.manage'] },
    { label: 'Analytics', permissions: ['analytics.view'] }
  ].filter(tab => tab.alwaysShow || tab.permissions.some(permission => hasPermission(permission)));

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const { stats, health, activities } = await AdminService.getDashboardOverview();
        
        setStats(stats);
        setSystemHealth(health);
        setRecentActivities(activities);
        
      } catch (err) {
        console.error('Error loading admin dashboard:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case 'healthy': return 'success';
      case 'warning': return 'warning';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy': return <CheckCircleIcon />;
      case 'warning': return <WarningIcon />;
      case 'critical': return <ErrorIcon />;
      default: return <CheckCircleIcon />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        <SettingsIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
        Admin Dashboard
      </Typography>

      {/* Navigation Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={handleTabChange}>
          {availableTabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Box>

      {/* Tab Content */}
      {currentTab === 0 && (
        <>
          {/* Stats Cards */}
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} sx={{ mb: 4 }}>
            {stats && (
              <>
                {renderIfPermission('user.view', (
                  <Box sx={{ flex: 1 }}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              Total Users
                            </Typography>
                            <Typography variant="h5" component="div">
                              {stats.total_users}
                            </Typography>
                          </Box>
                          <Box sx={{ color: 'primary.main' }}>
                            <PeopleIcon />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
                
                {renderIfPermission('project.view', (
                  <Box sx={{ flex: 1 }}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              Active Projects
                            </Typography>
                            <Typography variant="h5" component="div">
                              {stats.active_projects}
                            </Typography>
                          </Box>
                          <Box sx={{ color: 'success.main' }}>
                            <AnalyticsIcon />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
                
                {renderIfPermission('settings.view', (
                  <Box sx={{ flex: 1 }}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              System Health
                            </Typography>
                            <Typography variant="h5" component="div">
                              {systemHealth ? `${Math.round((systemHealth.memory_usage + systemHealth.cpu_usage) / 2)}%` : '98%'}
                            </Typography>
                          </Box>
                          <Box sx={{ color: systemHealth ? `${getHealthStatusColor(systemHealth.status)}.main` : 'info.main' }}>
                            {systemHealth ? getHealthStatusIcon(systemHealth.status) : <SecurityIcon />}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
                
                {renderIfPermission('audit.view', (
                  <Box sx={{ flex: 1 }}>
                    <Card>
                      <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <Box>
                            <Typography color="text.secondary" gutterBottom>
                              Server Status
                            </Typography>
                            <Typography variant="h5" component="div">
                              {systemHealth?.status === 'healthy' ? 'Online' : 'Issues'}
                            </Typography>
                          </Box>
                          <Box sx={{ color: 'success.main' }}>
                            <SettingsIcon />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Box>
                ))}
              </>
            )}
          </Box>

          {/* Quick Actions and Recent Activity */}
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} sx={{ mb: 4 }}>
            {/* Quick Actions */}
            <Box sx={{ flex: 1 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Quick Actions
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  {renderIfPermission('user.view', (
                    <Button 
                      variant="contained" 
                      startIcon={<PeopleIcon />} 
                      onClick={() => setCurrentTab(availableTabs.findIndex(tab => tab.label === 'User Management'))}
                      fullWidth
                      disabled={!availableTabs.some(tab => tab.label === 'User Management')}
                    >
                      Manage Users
                    </Button>
                  ))}
                  
                  {renderIfPermission('settings.view', (
                    <Button 
                      variant="outlined" 
                      startIcon={<SettingsIcon />}
                      onClick={() => setCurrentTab(availableTabs.findIndex(tab => tab.label === 'System Settings'))}
                      fullWidth
                      disabled={!availableTabs.some(tab => tab.label === 'System Settings')}
                    >
                      System Settings
                    </Button>
                  ))}
                  
                  {renderIfPermission('analytics.view', (
                    <Button 
                      variant="outlined" 
                      startIcon={<AnalyticsIcon />}
                      onClick={() => setCurrentTab(availableTabs.findIndex(tab => tab.label === 'Analytics'))}
                      fullWidth
                      disabled={!availableTabs.some(tab => tab.label === 'Analytics')}
                    >
                      View Analytics
                    </Button>
                  ))}
                  
                  {renderIfPermission('audit.view', (
                    <Button 
                      variant="outlined" 
                      startIcon={<SecurityIcon />}
                      fullWidth
                    >
                      Security Logs
                    </Button>
                  ))}
                </Box>
              </Paper>
            </Box>

            {/* System Status */}
            <Box sx={{ flex: 1 }}>
              <PermissionGuard requiredPermissions={['settings.view', 'audit.view']}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    System Status
                  </Typography>
                  <Box display="flex" flexDirection="column" gap={2}>
                    {systemHealth ? (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Application</Typography>
                          <Chip 
                            label={systemHealth.status === 'healthy' ? 'Running' : 'Issues'} 
                            color={getHealthStatusColor(systemHealth.status) as any}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Database</Typography>
                          <Chip 
                            label={systemHealth.services.database.status} 
                            color={systemHealth.services.database.status === 'healthy' ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Redis Cache</Typography>
                          <Chip 
                            label={systemHealth.services.redis.status} 
                            color={systemHealth.services.redis.status === 'healthy' ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Queue System</Typography>
                          <Chip 
                            label={systemHealth.services.queue.status} 
                            color={systemHealth.services.queue.status === 'healthy' ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Storage</Typography>
                          <Chip 
                            label={`${systemHealth.services.storage.usage_percentage}% Used`}
                            color={systemHealth.services.storage.usage_percentage > 80 ? 'warning' : 'info'}
                            size="small"
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="body2">Uptime</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {systemHealth.uptime}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Loading system status...
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </PermissionGuard>
            </Box>
          </Box>

          {/* Recent Activity */}
          <PermissionGuard requiredPermissions={['audit.view']}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent System Activity
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {recentActivities.length > 0 ? (
                  recentActivities.map((activity) => (
                    <Typography key={activity.id} variant="body2" color="text.secondary">
                      • {activity.description} 
                      {activity.user_name && ` by ${activity.user_name}`}
                      {activity.ip_address && ` from ${activity.ip_address}`}
                      ({new Date(activity.created_at).toLocaleString()})
                    </Typography>
                  ))
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      • System initialized successfully (startup)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Database connection established (startup)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Redis cache connected (startup)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Application ready for requests (startup)
                    </Typography>
                  </>
                )}
              </Box>
            </Paper>
          </PermissionGuard>
        </>
      )}

      {/* Render other tabs based on available permissions */}
      {availableTabs[currentTab] && (
        <>
          {availableTabs[currentTab].label === 'AI Console' && (
            <Box sx={{ height: '70vh' }}>
              <AIConsole defaultContextType="general" />
            </Box>
          )}
          {availableTabs[currentTab].label === 'User Management' && hasPermission('user.view') && (
            <PermissionGuard requiredPermissions={['user.view']}>
              <UserManagement />
            </PermissionGuard>
          )}
          {availableTabs[currentTab].label === 'Departments' && hasPermission('settings.view') && (
            <PermissionGuard requiredPermissions={['settings.view']}>
              <DepartmentManagement />
            </PermissionGuard>
          )}
          {availableTabs[currentTab].label === 'AI Configuration' && hasPermission('settings.view') && (
            <PermissionGuard requiredPermissions={['settings.view']}>
              <AIConfigurationManager />
            </PermissionGuard>
          )}
          {availableTabs[currentTab].label === 'AI Usage & Costs' && (hasPermission('settings.view') || hasPermission('analytics.view')) && (
            <PermissionGuard requiredPermissions={['settings.view', 'analytics.view']} requireAll={false}>
              <AIUsageMonitor />
            </PermissionGuard>
          )}
          {availableTabs[currentTab].label === 'System Settings' && hasPermission('settings.view') && (
            <PermissionGuard requiredPermissions={['settings.view']}>
              <SystemSettings />
            </PermissionGuard>
          )}
          {availableTabs[currentTab].label === 'Analytics' && hasPermission('analytics.view') && (
            <PermissionGuard requiredPermissions={['analytics.view']}>
              <Analytics />
            </PermissionGuard>
          )}
        </>
      )}
    </Container>
  );
};

export default AdminDashboard;